import { useState } from 'react';
import { motion } from 'motion/react';
import { Crown, MapPin, Users, Calendar, Heart, CheckCircle, Loader2, ExternalLink, ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SKOOL_URL = "https://www.skool.com/letter-writers-society-5504";

const AU_STATES = [
  { value: "NSW", label: "New South Wales" },
  { value: "VIC", label: "Victoria" },
  { value: "QLD", label: "Queensland" },
  { value: "WA", label: "Western Australia" },
  { value: "SA", label: "South Australia" },
  { value: "TAS", label: "Tasmania" },
  { value: "ACT", label: "Australian Capital Territory" },
  { value: "NT", label: "Northern Territory" },
  { value: "OUTSIDE_AU", label: "Outside Australia" }
];

const HOW_HEARD_OPTIONS = [
  { value: "instagram", label: "Instagram" },
  { value: "gathering", label: "Attended a gathering" },
  { value: "course", label: "Course or membership" },
  { value: "friend", label: "Friend referral" },
  { value: "other", label: "Other" }
];

const RELATIONSHIP_OPTIONS = [
  { value: "correspondent", label: "I'm a Correspondence Method member" },
  { value: "pen_friend", label: "I'm a $5 Pen Friend member" },
  { value: "gathering_attendee", label: "I've attended a gathering" },
  { value: "not_yet", label: "Not yet a member, but I love what you're building" }
];

const CHAPTER_FORMAT_OPTIONS = [
  { value: "in_person", label: "In-person — I have a venue in mind" },
  { value: "online", label: "Online — I'd host via video call" },
  { value: "hybrid", label: "Hybrid — in-person with online members joining" },
  { value: "unsure", label: "Not sure yet — I'd like to discuss options" }
];

const FREQUENCY_OPTIONS = [
  { value: "monthly", label: "Monthly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "weekly", label: "Weekly" },
  { value: "flexible", label: "Flexible / not sure yet" }
];

export default function PostmasterApplicationPage() {
  const [formData, setFormData] = useState({
    // Section 1 - About You
    fullName: '',
    email: '',
    phone: '',
    city: '',
    state: '',
    instagram: '',
    howHeard: '',
    howHeardOther: '',
    
    // Section 2 - Your Connection
    relationship: '',
    attendedGathering: '',
    gatheringDetails: '',
    
    // Section 3 - The Chapter You'd Run
    chapterFormat: '',
    venueIdeas: '',
    frequency: '',
    idealDay: '',
    
    // Section 4 - Your Vision
    whyPostmaster: '',
    communityVision: '',
    
    // Section 5 - Availability
    canCommit: '',
    startTimeline: '',
    questions: ''
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/postmaster/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        setSubmitted(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to submit application');
      }
    } catch (err) {
      setError('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-lg w-full bg-white rounded-2xl shadow-lg p-8 text-center"
        >
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-3xl font-serif text-stone-800 mb-4">Application Received!</h1>
          <p className="text-stone-600 mb-6">
            Thank you for your interest in becoming a Postmaster. Frederica will review your application and be in touch soon.
          </p>
          <p className="text-sm text-stone-500 mb-8">
            In the meantime, join our community to connect with other letter writers.
          </p>
          <div className="space-y-3">
            <a
              href={SKOOL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 transition-colors"
            >
              Join the Skool Community
              <ExternalLink className="w-4 h-4" />
            </a>
            <a
              href="#home"
              className="block w-full py-3 border-2 border-stone-300 text-stone-600 rounded-lg font-medium hover:bg-stone-50 transition-colors"
            >
              Return Home
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="postmaster-application-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-purple-900 to-purple-800 text-white py-20 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-purple-700/50 px-4 py-2 rounded-full text-sm mb-6">
            <Crown className="w-4 h-4" />
            Become a Postmaster
          </div>
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-serif mb-6"
          >
            Carry LWS Into Your Community
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-purple-200 max-w-2xl mx-auto"
          >
            Postmasters are not simply senior members — they are carriers of the LWS philosophy. They choose to extend the Society into their community.
          </motion.p>
        </div>
      </section>

      {/* Role Overview */}
      <section className="py-16 px-6 bg-white border-b border-stone-200">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-serif text-stone-800 mb-8 text-center">What You'll Receive</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium text-stone-800 mb-1">Complimentary Membership</h3>
                <p className="text-sm text-stone-600">Full Correspondent access with no annual fee while actively facilitating</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium text-stone-800 mb-1">Revenue Share</h3>
                <p className="text-sm text-stone-600">Earn from letter bar sales and chapter memberships you generate</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium text-stone-800 mb-1">Chapter Starter Kit</h3>
                <p className="text-sm text-stone-600">LWS sandwich board, monthly prompts, and seasonal product drops</p>
              </div>
            </div>
            
            <div className="flex items-start gap-4 p-4 bg-stone-50 rounded-lg">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Heart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-medium text-stone-800 mb-1">Direct Access</h3>
                <p className="text-sm text-stone-600">Connect directly with Frederica and the LWS team</p>
              </div>
            </div>
          </div>

          <div className="mt-8 p-6 bg-purple-50 rounded-xl border border-purple-100">
            <h3 className="font-medium text-purple-900 mb-3">Your Responsibilities</h3>
            <ul className="space-y-2 text-sm text-purple-800">
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                Embody and uphold LWS values and culture at every gathering
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                Book and administer your venue or online space for monthly chapter meetings
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                List and manage chapter events within the LWS system
              </li>
              <li className="flex items-start gap-2">
                <span className="text-purple-500 mt-1">•</span>
                Receive, manage, and distribute seasonal product drops
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-2xl font-serif text-stone-800 mb-8 text-center">Application Form</h2>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-10">
            {/* Section 1 - About You */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-2 border-b border-stone-200">
                <span className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">1</span>
                <h3 className="text-lg font-medium text-stone-800">About You</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Full Name *</label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="input-fullName"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="input-email"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="input-phone"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">City / Suburb *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="input-city"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">State / Territory *</label>
                  <div className="relative">
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                      data-testid="select-state"
                    >
                      <option value="">Select...</option>
                      {AU_STATES.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Instagram or Social Handle</label>
                  <input
                    type="text"
                    name="instagram"
                    value={formData.instagram}
                    onChange={handleChange}
                    placeholder="@"
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    data-testid="input-instagram"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">How did you hear about Letter Writers Society? *</label>
                <div className="relative">
                  <select
                    name="howHeard"
                    value={formData.howHeard}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                    data-testid="select-howHeard"
                  >
                    <option value="">Select...</option>
                    {HOW_HEARD_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="w-4 h-4 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
                {formData.howHeard === 'other' && (
                  <input
                    type="text"
                    name="howHeardOther"
                    value={formData.howHeardOther}
                    onChange={handleChange}
                    placeholder="Please specify..."
                    className="mt-2 w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                )}
              </div>
            </div>

            {/* Section 2 - Your Connection */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-2 border-b border-stone-200">
                <span className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">2</span>
                <h3 className="text-lg font-medium text-stone-800">Your Connection to Letter Writers Society</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">What's your current relationship with LWS? *</label>
                <div className="space-y-2">
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-50 transition-colors">
                      <input
                        type="radio"
                        name="relationship"
                        value={opt.value}
                        checked={formData.relationship === opt.value}
                        onChange={handleChange}
                        required
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-stone-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">Have you attended an LWS gathering before? *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="attendedGathering"
                      value="yes"
                      checked={formData.attendedGathering === 'yes'}
                      onChange={handleChange}
                      required
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-stone-700">Yes</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="attendedGathering"
                      value="no"
                      checked={formData.attendedGathering === 'no'}
                      onChange={handleChange}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-stone-700">No</span>
                  </label>
                </div>
                {formData.attendedGathering === 'yes' && (
                  <input
                    type="text"
                    name="gatheringDetails"
                    value={formData.gatheringDetails}
                    onChange={handleChange}
                    placeholder="Which gathering(s) and where?"
                    className="mt-3 w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                )}
              </div>
            </div>

            {/* Section 3 - The Chapter You'd Run */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-2 border-b border-stone-200">
                <span className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">3</span>
                <h3 className="text-lg font-medium text-stone-800">The Chapter You'd Run</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">What format would your chapter take? *</label>
                <div className="space-y-2">
                  {CHAPTER_FORMAT_OPTIONS.map(opt => (
                    <label key={opt.value} className="flex items-center gap-3 p-3 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-50 transition-colors">
                      <input
                        type="radio"
                        name="chapterFormat"
                        value={opt.value}
                        checked={formData.chapterFormat === opt.value}
                        onChange={handleChange}
                        required
                        className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                      />
                      <span className="text-stone-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              
              {(formData.chapterFormat === 'in_person' || formData.chapterFormat === 'hybrid') && (
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Tell us about your venue ideas</label>
                  <textarea
                    name="venueIdeas"
                    value={formData.venueIdeas}
                    onChange={handleChange}
                    placeholder="Café, library, your home, co-working space..."
                    rows={3}
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                </div>
              )}
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">How often would you meet? *</label>
                  <div className="relative">
                    <select
                      name="frequency"
                      value={formData.frequency}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white"
                    >
                      <option value="">Select...</option>
                      {FREQUENCY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                    <ChevronDown className="w-4 h-4 text-stone-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-stone-700 mb-1">Ideal day of the week?</label>
                  <input
                    type="text"
                    name="idealDay"
                    value={formData.idealDay}
                    onChange={handleChange}
                    placeholder="e.g., Sunday afternoons"
                    className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Section 4 - Your Vision */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-2 border-b border-stone-200">
                <span className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">4</span>
                <h3 className="text-lg font-medium text-stone-800">Your Vision</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Why do you want to become a Postmaster? *</label>
                <textarea
                  name="whyPostmaster"
                  value={formData.whyPostmaster}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="What draws you to this role? What would it mean to you?"
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  data-testid="textarea-whyPostmaster"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">What kind of community would you like to build? *</label>
                <textarea
                  name="communityVision"
                  value={formData.communityVision}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Describe the atmosphere, the people, the feeling..."
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  data-testid="textarea-communityVision"
                />
              </div>
            </div>

            {/* Section 5 - Availability */}
            <div className="space-y-6">
              <div className="flex items-center gap-3 pb-2 border-b border-stone-200">
                <span className="w-8 h-8 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-sm font-medium">5</span>
                <h3 className="text-lg font-medium text-stone-800">Availability & Commitment</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">Can you commit to hosting at least one gathering per month? *</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="canCommit"
                      value="yes"
                      checked={formData.canCommit === 'yes'}
                      onChange={handleChange}
                      required
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-stone-700">Yes, absolutely</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="canCommit"
                      value="discuss"
                      checked={formData.canCommit === 'discuss'}
                      onChange={handleChange}
                      className="w-4 h-4 text-purple-600 focus:ring-purple-500"
                    />
                    <span className="text-stone-700">I'd like to discuss</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">When could you realistically start? *</label>
                <input
                  type="text"
                  name="startTimeline"
                  value={formData.startTimeline}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Next month, in 3 months, after the new year..."
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  data-testid="input-startTimeline"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Any questions for us?</label>
                <textarea
                  name="questions"
                  value={formData.questions}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Anything you'd like to know before we chat?"
                  className="w-full px-4 py-2.5 border border-stone-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                />
              </div>
            </div>

            {/* Submit */}
            <div className="pt-6 border-t border-stone-200">
              <Button
                type="submit"
                disabled={submitting}
                className="w-full py-4 bg-purple-600 hover:bg-purple-700 text-white text-lg font-medium rounded-lg transition-colors"
                data-testid="submit-application"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  'Submit Application'
                )}
              </Button>
              <p className="mt-4 text-center text-sm text-stone-500">
                We'll review your application and be in touch within 5-7 days.
              </p>
            </div>
          </form>
        </div>
      </section>

      {/* Join Community CTA */}
      <section className="py-12 px-6 bg-purple-50 border-t border-purple-100">
        <div className="max-w-2xl mx-auto text-center">
          <p className="text-purple-800 mb-4">
            Not ready to apply yet? Join our community first and get to know us.
          </p>
          <a
            href={SKOOL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-purple-700 font-medium hover:text-purple-800 transition-colors"
          >
            Visit our Skool Community
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </section>
    </div>
  );
}
